import React from 'react';
import { StyleSheet } from 'react-native';
import {
  EnumCellProps,
  isEnumControl,
  JsonFormsCellRendererRegistryEntry,
  RankedTester,
  rankWith,
} from '@jsonforms/core';
import { TranslateProps, withJsonFormsEnumCellProps, withTranslateProps } from '@jsonforms/react';
import Colors from '../../../../constants/Colors';
import { Picker } from '@react-native-picker/picker';
import { RENDERER_RANK_PREFERRED_MATCH } from '../constants';

const styles = StyleSheet.create({
  dropdown: {
    width: '100%',
    backgroundColor: Colors.lightBackground,
    justifyContent: 'space-between',
    fontSize: 16,
    color: Colors.veryDarkGray,
    borderColor: Colors.grayBorder,
    paddingTop: 4,
    paddingBottom: 4,
  },
  errorDropdown: {
    borderColor: Colors.brightRed,
  },
});

/**
 * Tester to determine if a control can be used with this cell.
 */
const tester: RankedTester = rankWith(RENDERER_RANK_PREFERRED_MATCH, isEnumControl);

/**
 * Base cell renderer to show a single-select enum input control.
 * @param props Control props to be hydrated by context.
 */
export const EnumCellRenderer = (props: EnumCellProps & TranslateProps) => {
  return (
    <Picker
      mode="dropdown"
      testID={`EnumCell-${props.id}`}
      selectedValue={props.data}
      onValueChange={(newValue) => props.handleChange(props.path, newValue)}
      enabled={props.enabled}
      style={{
        ...styles.dropdown,
        ...(props.errors.length > 0 ? styles.errorDropdown : {}),
      }}
      dropdownIconColor={Colors.veryDarkGray}
    >
      <Picker.Item
        testID={`EnumCell-${props.id}-none`}
        value={''}
        label={
          // similar to vanilla renderer allow for custom translations of default key
          props.t('enum.none', '', { schema: props.schema, uischema: props.uischema, path: props.path })
        }
        key={
          // use same default key from vanilla renderers
          'jsonforms.enum.none'
        }
      />
      {props.options &&
        props.options.length > 0 &&
        props.options.map((enumOption) => (
          <Picker.Item
            testID={`EnumCell-${props.id}-option-${enumOption.value}`}
            value={enumOption.value}
            label={enumOption.label}
            key={
              // use value as key just like vanilla renderers
              enumOption.value
            }
          />
        ))}
    </Picker>
  );
};

/**
 * Renderer registry entry for input control.
 * Bundles the tester and the wrapped control for use with form.
 */
export const EnumCell: JsonFormsCellRendererRegistryEntry = {
  tester,
  cell: withJsonFormsEnumCellProps(withTranslateProps(EnumCellRenderer)),
};
